import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOJsonLd from '@/components/feature/SEOJsonLd';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const relatedComics = comics.filter((c) => c.categorySlug === comic?.categorySlug && c.id !== id).slice(0, 3);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${comic.title} - ${comic.author} | 韩漫网站在线免费阅读`;
  const pageDesc = `在线免费阅读${comic.title}（${comic.author}）。${comic.description.slice(0, 100)}... 韩漫网站提供最新韩国漫画免费在线观看。`;

  const pageJsonLd = {
    name: pageTitle,
    description: pageDesc,
    url: `http://guyunqutang.com/comic/${comic.id}`,
    inLanguage: 'zh-CN',
  };

  const articleJsonLd = {
    '@type': 'Article',
    headline: comic.title,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updateTime,
    description: comic.description,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOJsonLd type="WebPage" data={pageJsonLd} />
      <SEOJsonLd type="Article" data={articleJsonLd} />
      <header>
        <Navbar />
      </header>
      <main>
        <section className="pt-24 pb-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: comic.category, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <div className="mt-6 flex flex-col lg:flex-row gap-10">
              <div className="lg:w-[340px] flex-shrink-0">
                <div className="relative -ml-4 lg:-ml-12">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full max-w-[280px] aspect-[3/4] object-cover object-top rounded-xl"
                  />
                </div>
              </div>

              <div className="flex-1 pt-2">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/10 text-accent-700 border border-accent-300/30'
                      : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="text-xs text-foreground-500">
                    共 {comic.chapters} 话
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-eye-line"></i> {comic.views}
                  </span>
                </div>

                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-6">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-500 text-lg"></i>
                    <span className="text-xl font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">/ 5.0</span>
                  </div>
                  <span className="text-xs text-foreground-400">|</span>
                  <span className="text-xs text-foreground-500">更新于 {comic.updateTime}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full border border-background-200/60"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-8 border border-background-200/60">
                  <h4 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-book-open-line mr-2"></i>开始阅读第1话
                  </button>
                  <button className="flex-1 bg-background-100 text-foreground-700 text-sm font-medium px-6 py-3 rounded-full border border-background-200/60 hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-2"></i>加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedComics.map((r) => (
                  <Link
                    key={r.id}
                    to={`/comic/${r.id}`}
                    className="group relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={r.coverImage}
                      alt={r.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-base font-heading font-bold text-background-50 mb-1">{r.title}</h3>
                      <span className="text-xs text-background-300">{r.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              开启阅读之旅
            </h2>
            <p className="text-sm text-foreground-600 mb-8">
              更多精彩漫画等你来发现
            </p>
            <Link
              to="/category/romance"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              探索更多漫画 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}